import { hexToRgbA } from 'helpers/global.helper/global.helper'
import { MRT_PaginationState } from 'material-react-table'
import moment from 'moment'
import { colors as variablesColors } from 'styles/variables'
import { getTimeSeriesClipboardCellId } from './TimeSeriesDataTable.clipboard.adapter'
import { TimeSeriesDataTableColumnDef, TimeSeriesDataTableData } from './types'

export function customStringToISODateTime(customString: string): string {
  return moment(customString, 'D MMM HH:mm').toISOString(true)
}

export function cropDataToPagination(
  data: TimeSeriesDataTableData[],
  pagination: MRT_PaginationState
): TimeSeriesDataTableData[] {
  let croppedData = data.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize
  )

  // Remove all rows that only have values '-', these are dummy rows to ensure current hour is on line 0 on any page
  croppedData = croppedData.filter((row) => {
    return Object.values(row).some((value) => value !== '-')
  })

  return croppedData
}

export function hasSameSelection(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) {
    return false
  }

  for (const cellId of left) {
    if (!right.has(cellId)) {
      return false
    }
  }

  return true
}

export function getSelectionBorderShadow(
  isSelected: boolean,
  selectionRowIndex: number,
  selectionColumnIndex: number | undefined,
  selectedCells: Set<string>
): string {
  if (!isSelected || selectionColumnIndex === undefined) {
    return ''
  }

  const hasSelectedTopNeighbor = selectedCells.has(
    getTimeSeriesClipboardCellId(selectionRowIndex - 1, selectionColumnIndex)
  )
  const hasSelectedBottomNeighbor = selectedCells.has(
    getTimeSeriesClipboardCellId(selectionRowIndex + 1, selectionColumnIndex)
  )
  const hasSelectedLeftNeighbor = selectedCells.has(
    getTimeSeriesClipboardCellId(selectionRowIndex, selectionColumnIndex - 1)
  )
  const hasSelectedRightNeighbor = selectedCells.has(
    getTimeSeriesClipboardCellId(selectionRowIndex, selectionColumnIndex + 1)
  )

  const selectionBorderColor = hexToRgbA(variablesColors.info4, 0.95)
  const selectionBorderShadows: string[] = []

  if (!hasSelectedTopNeighbor) {
    selectionBorderShadows.push(`inset 0 2px 0 0 ${selectionBorderColor}`)
  }
  if (!hasSelectedBottomNeighbor) {
    selectionBorderShadows.push(`inset 0 -2px 0 0 ${selectionBorderColor}`)
  }
  if (!hasSelectedLeftNeighbor) {
    selectionBorderShadows.push(`inset 2px 0 0 0 ${selectionBorderColor}`)
  }
  if (!hasSelectedRightNeighbor) {
    selectionBorderShadows.push(`inset -2px 0 0 0 ${selectionBorderColor}`)
  }

  return selectionBorderShadows.join(', ')
}

type ResolveTimeSeriesCellBackgroundColorParams = {
  rowIsCurrentOptHour: boolean
  rowIsAfterCurrentOptHour: boolean
  markHoursAhead: boolean
  useColumnColorFromItem?: boolean
  thisColumnIndex: number
  columnDef: TimeSeriesDataTableColumnDef
}

export function resolveTimeSeriesCellBackgroundColor({
  rowIsCurrentOptHour,
  rowIsAfterCurrentOptHour,
  markHoursAhead,
  useColumnColorFromItem,
  thisColumnIndex,
  columnDef,
}: ResolveTimeSeriesCellBackgroundColorParams): string {
  if (rowIsCurrentOptHour) {
    return variablesColors.info2
  }

  if (rowIsAfterCurrentOptHour && markHoursAhead) {
    return variablesColors.success2
  }

  if (useColumnColorFromItem && columnDef?.color) {
    return hexToRgbA(columnDef.color ?? '#ffffff', 0.35)
  }

  if (!useColumnColorFromItem && thisColumnIndex % 2) {
    return 'rgba(242, 242, 242, 1)'
  }

  return 'rgba(255, 255, 255, 1)'
}

type BuildTimeSeriesSelectionVisualStateParams = {
  columnId: string
  selectableColumnIdToIndex: Record<string, number>
  selectionRowIndex: number
  selectedCells: Set<string>
}

export function buildTimeSeriesSelectionVisualState({
  columnId,
  selectableColumnIdToIndex,
  selectionRowIndex,
  selectedCells,
}: BuildTimeSeriesSelectionVisualStateParams): {
  selectionColumnIndex: number | undefined
  isSelected: boolean
  boxShadowValue: string
} {
  const selectionColumnIndex = selectableColumnIdToIndex[columnId]
  const selectionCellId =
    selectionColumnIndex === undefined ? null : getTimeSeriesClipboardCellId(selectionRowIndex, selectionColumnIndex)
  const isSelected = selectionCellId ? selectedCells.has(selectionCellId) : false
  const boxShadowValue = getSelectionBorderShadow(isSelected, selectionRowIndex, selectionColumnIndex, selectedCells)

  return {
    selectionColumnIndex,
    isSelected,
    boxShadowValue,
  }
}
